import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Select,
  Pagination,
  Row,
  Col,
  Space,
} from "antd";
import { EditOutlined, SaveOutlined, SearchOutlined, StopOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { SEGMENTS } from "../../../Helper/constant";
import { useDispatch, useSelector } from "react-redux";
import APICall from "../../../APICall";
import EndPoints from "../../../APICall/EndPoints";
import {
  setBlockedScripts,
  setEditData,
  setDeletedScripts,
} from "../../../store/slice/scriptSettingsRequestSlice";
// removed useMutation: no longer needed (single API flow)
import { useWatch } from "antd/es/form/Form";
// removed useGetBarScript: using single API call and frontend pagination
import ScriptSettingsForm from "./MarketAccess/ScriptSettingsForm";
import "./FooterView.css";

const _ = require("lodash");

const FooterView = ({ tab, view, parentForm, activeKey }) => {
  const wrapperRef = useRef(null);

  return (
    <div>
      <WrapperView
        ref={wrapperRef}
        tab={tab}
        view={view}
        parentForm={parentForm}
        activeKey={activeKey}
      />
    </div>
  );
};

export default FooterView;

const WrapperView = (props, _ref) => {
  const { tab, parentForm, activeKey } = props;
  const dataOffset = useRef(0);
  const valueRef = useRef([]);
  const dispatch = useDispatch();
  let canCallAPI = true;
  const isEQUOrCRYPTO = ["NSE EQU", "CRYPTO"].includes(tab);
  //state
  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [form] = Form.useForm();
  const [searchValue, setSearchValue] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const [selectedRow, setSelectedRow] = useState([]);
  const [sort, setSort] = useState({ column: null, order: null });
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [editScriptData, setEditScriptData] = useState(null);

  // State for all scripts data
  const [allScriptsData, setAllScriptsData] = useState([]);
  const [allScriptsLoading, setAllScriptsLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [blockedData, setBlockedData] = useState([]);
  const [availableScripts, setAvailableScripts] = useState([]);
  const [showBlockScriptModal, setShowBlockScriptModal] = useState(false);
  const [unblockedScripts, setUnblockedScripts] = useState([]);
  const [isAddingNewScript, setIsAddingNewScript] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedScriptValue, setSelectedScriptValue] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [showRangeSettingsModal, setShowRangeSettingsModal] = useState(false);
  const [rangeSettingsData, setRangeSettingsData] = useState(null);
  const [deletedScriptsList, setDeletedScriptsList] = useState([]);

  // Redux: script settings (ensure available before any effects use it)
  const scriptSettingsRequest = useSelector(
    (state) => state.scriptSettingsRequestSlice?.[tab] || {}
  );
  const staticBlocked = useMemo(
    () => scriptSettingsRequest?.staticBlocked || [],
    [scriptSettingsRequest?.staticBlocked]
  );

  // Fetch all scripts once (single API) and do search/sort/pagination on frontend
  const fetchAllScripts = async () => {
    if (!activeKey) return;

    setAllScriptsLoading(true);
    try {
      let url = `${EndPoints.getTableScript}${SEGMENTS[tab]}`;

      const response = await APICall("get", url);
      if (response?.status === 200) {
        const scriptsData = response?.data?.data?.data || [];
        setAllScriptsData(scriptsData);
      } else {
        notification.error({ message: response?.data?.message });
      }
    } catch (error) {
      notification.error({ message: error?.message });
    } finally {
      setAllScriptsLoading(false);
    }
  };

  // Fetch all scripts when tab toggles
  useEffect(() => {
    fetchAllScripts();
  }, [tab, activeKey]);

  // Derive available scripts from single dataset
  useEffect(() => {
    if (!allScriptsData?.length) {
      setAvailableScripts([]);
      return;
    }
    const existingNames = new Set([
      ...filteredData.map(i => i.scriptName),
      ...blockedData.map(i => i.scriptName),
    ]);
    const available = allScriptsData.filter(s => !existingNames.has(s.symbolName));
    setAvailableScripts(available);
  }, [allScriptsData, filteredData, blockedData]);

  // Handle window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clear dropdown selection when script modal is closed
  useEffect(() => {
    if (!showScriptModal) {
      setSelectedScriptValue(null);
    }
  }, [showScriptModal]);

  // Derive unblocked scripts for blocking UI from single dataset
  useEffect(() => {
    if (!allScriptsData?.length) {
      setUnblockedScripts([]);
      return;
    }
    const blockedNames = new Set([
      ...blockedData.map(item => item.scriptName),
      ...staticBlocked,
    ]);
    const unblocked = allScriptsData.filter(s => !blockedNames.has(s.symbolName));
    setUnblockedScripts(unblocked);
  }, [allScriptsData, blockedData, staticBlocked]);



  // Function to add script to blocked list
  const addScriptToBlocked = (script) => {
    const newBlockedScript = {
      id: blockedData.length + 1,
      scriptName: script.symbolName,
      script_name_value: script.symbol,
      minValue: null,
      maxValue: null,
      delivery: null,
      intraday: null,
      total: null,
      type: isEQUOrCRYPTO ? "quantity" : "lot",
      open: script.open,
      lotSize: script.lotSize,
      brokerOneIdDelivery: null,
      brokerOneIdIntraday: null,
      brokerTwoIdDelivery: null,
      brokerTwoIdIntraday: null,
      brokerThreeIdDelivery: null,
      brokerThreeIdIntraday: null,
    };

    setBlockedData(prev => [...prev, newBlockedScript]);
    setSelectedRow(prev => [...prev, script.symbolName]);
  };



  // removed react-query bar hook: using single fetchAllScripts

  //redux
  const loginId = useSelector((state) => state.authSlice?.userDetail?.userId);
  const accountType = useSelector(
    (state) => state.userAccountSlice?.accountType
  );

  // Update expandedRowKeys when accountType changes
  useEffect(() => {
    setExpandedRowKeys(accountType === "customer" ? ["all-row"] : []);
  }, [accountType]);

  const search = useSelector((state) => state?.scriptSettingSlice?.search);
  const scriptInfo = useSelector(
    (state) =>
      state?.userAccountSlice?.[tab]?.["scriptWiseSettingInfo"]?.selectedScript
  );
  const apidata = useSelector(
    (state) => state?.userAccountSlice?.[tab]?.brokerageDetails
  );

  const initalDelivery = apidata?.deliveryTotal;
  const initalIntraday = apidata?.intradayTotal;

  const watchIntraday = useWatch(`intradayTotal$${tab}`, parentForm);
  const watchDelivery = useWatch(`deliveryTotal$${tab}`, parentForm);

  // previous name scripSettings now markteWiseAllScript
  const marketWiseAllScript = useSelector(
    (state) => state.scriptSettingSlice?.[tab]
  );

  const parentScriptS = useSelector(
    (state) => state.userAccountSlice.parentValidation
  );
  const brokarageDetails = useSelector(
    (state) => state.userAccountSlice?.parentValidation?.brokarageDetails?.[tab]
  );
  const watchBrokerageType = useWatch(`brokerageType$${tab}`, parentForm);

  const userAccountType = useSelector(
    (state) => state.userAccountSlice.accountType
  );
  const isRent = parentScriptS?.accountDetails?.partnershipWith === "rent";

  // duplicate removed; declarations are hoisted earlier to avoid TDZ issues

  const [extraRow, setExtraRow] = useState({
    key: "0",
    id: "All",
    block: false,
    scriptName: "All",
    open: "",
    lotSize: "",
    delivery: watchDelivery || initalDelivery || null,
    intraday: watchIntraday || initalIntraday || null,
    type: isEQUOrCRYPTO ? "quantity" : "lot",
    minValue: null,
    maxValue: null,
    total: null,
    brokerOneIdDelivery: null,
    brokerOneIdIntraday: null,
    brokerTwoIdDelivery: null,
    brokerTwoIdIntraday: null,
    brokerThreeIdDelivery: null,
    brokerThreeIdIntraday: null,
  });

  // Helper to get short label for type (L / Q / V)
  const getTypeLabel = (type) => {
    if (!type) return "";
    if (type === "lot") return "Lot";
    if (type === "quantity") return "Qty";
    if (type === "value") return "Val";
    return type?.[0]?.toUpperCase?.() || "";
  };

  // Helper function to handle zero values
  const processValue = (value) => (value === 0 ? 0 : Number(value) || null);

  // Helper: check if a settings object has at least one non-null limit/field
  const checkSettingsHasValues = useCallback((settingsObj) => {
    if (!settingsObj) return false;
    const has = (v) => v !== null && v !== undefined;
    return (
      has(settingsObj.minValue) ||
      has(settingsObj.maxValue) ||
      has(settingsObj.delivery) ||
      has(settingsObj.intraday) ||
      has(settingsObj.total) ||
      has(settingsObj.brokerOneIdDelivery) ||
      has(settingsObj.brokerOneIdIntraday) ||
      has(settingsObj.brokerTwoIdDelivery) ||
      has(settingsObj.brokerTwoIdIntraday) ||
      has(settingsObj.brokerThreeIdDelivery) ||
      has(settingsObj.brokerThreeIdIntraday)
    );
  }, []);

  // Show card if script has settings in initial/response data (marketWiseAllScript).
  const hasSettingsFromResponse = useCallback((scriptName) => {
    const settings = marketWiseAllScript?.[scriptName];
    return checkSettingsHasValues(settings);
  }, [marketWiseAllScript, checkSettingsHasValues]);

  // Show card if user added/edited scriptwise settings via the Edit Script Settings modal (scriptSettingsRequest.edited).
  // This ensures new cards appear when user adds or edits via the modal; unblock-only does not add (empty entry not in edited with values).
  const hasSettingsFromEdited = useCallback((scriptName) => {
    if (scriptName === "All") return false;
    const editedItem = scriptSettingsRequest?.edited?.find((item) => item.scriptName === scriptName);
    return checkSettingsHasValues(editedItem);
  }, [scriptSettingsRequest?.edited, checkSettingsHasValues]);

  // Helper function to check if script is blocked
  const isBlocked = useCallback((scriptName) => {
    return staticBlocked?.includes(scriptName) ||
      scriptSettingsRequest?.blocked?.includes(scriptName);
  }, [staticBlocked, scriptSettingsRequest?.blocked]);

  // Memoized data processing for better performance
  const processedData = useMemo(() => {
    if (!allScriptsData || allScriptsData.length === 0) {
      return {
        filteredData: [],
        blockedData: [],
        totalCount: 0
      };
    }

    // Find global settings for "All" scripts
    const globalSettings = scriptSettingsRequest?.edited.find(
      (item) => item.scriptName === "All"
    );

    // Apply client-side search on symbolName
    const searched = (allScriptsData || []).filter(item =>
      searchValue ? (item.symbolName?.toLowerCase().includes(searchValue.toLowerCase())) : true
    );

    // Apply client-side sort on raw data from API
    const sorted = (() => {
      if (sort?.column && sort?.order) {
        const copy = [...searched];
        copy.sort((a, b) => {
          let aVal = a[sort.column];
          let bVal = b[sort.column];
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return sort.order === 'asc' ? aVal - bVal : bVal - aVal;
          }
          aVal = String(aVal || '').toLowerCase();
          bVal = String(bVal || '').toLowerCase();
          return sort.order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
        return copy;
      }
      return searched;
    })();

    // Separate blocked scripts from scripts with settings on the sorted dataset
    const blockedScripts = sorted.filter(item => isBlocked(item.symbolName));
    // Include script-wise card if script is not blocked AND has settings in response OR was added/edited via the modal.
    // Response: show card for scripts that already had scriptwise settings in initial data.
    // Edited: show card when user adds or edits scriptwise setting via Edit Script Settings modal (adds to edited with values).
    // Unblock-only does not add a card (no entry in edited with values).
    const scriptsWithSettings = sorted.filter(item => {
      if (isBlocked(item.symbolName)) return false;
      return hasSettingsFromResponse(item.symbolName) || hasSettingsFromEdited(item.symbolName);
    });

    // Transform data for scripts with settings
    const transformedData = scriptsWithSettings.map((item, index) => {
      const symbolSettings = marketWiseAllScript[item.symbolName] || {};
      const editedSettings = scriptSettingsRequest?.edited?.find(edit => edit.scriptName === item.symbolName) || {};

      // Merge settings from both sources, with edited settings taking precedence
      const mergedSettings = {
        ...symbolSettings,
        ...editedSettings
      };

      const {
        minValue = null,
        maxValue = null,
        delivery = null,
        intraday = null,
        total = null,
        type,
        brokerOneIdDelivery,
        brokerOneIdIntraday,
        brokerTwoIdDelivery,
        brokerTwoIdIntraday,
        brokerThreeIdDelivery,
        brokerThreeIdIntraday,
      } = mergedSettings;

      return {
        id: index + 1,
        _id: item._id,
        scriptName: item.symbolName,
        script_name_value: item.symbol,
        minValue: processValue(minValue),
        maxValue: processValue(maxValue),
        delivery: processValue(delivery),
        intraday: processValue(intraday),
        total: processValue(total),
        type: type || (isEQUOrCRYPTO ? "quantity" : "lot"),
        open: item.open,
        lotSize: item.lotSize,
        brokerOneIdDelivery: brokerOneIdDelivery || null,
        brokerOneIdIntraday: brokerOneIdIntraday || null,
        brokerTwoIdDelivery: brokerTwoIdDelivery || null,
        brokerTwoIdIntraday: brokerTwoIdIntraday || null,
        brokerThreeIdDelivery: brokerThreeIdDelivery || null,
        brokerThreeIdIntraday: brokerThreeIdIntraday || null,
        checked: scriptInfo || false,
      };
    });

    // Transform blocked scripts data
    const transformedBlockedData = blockedScripts.map((item, index) => {
      const symbolSettings = marketWiseAllScript[item.symbolName] || {};
      const editedSettings = scriptSettingsRequest?.edited?.find(edit => edit.scriptName === item.symbolName) || {};

      // Merge settings from both sources, with edited settings taking precedence
      const mergedSettings = {
        ...symbolSettings,
        ...editedSettings
      };

      const {
        minValue = null,
        maxValue = null,
        delivery = null,
        intraday = null,
        total = null,
        type,
        brokerOneIdDelivery,
        brokerOneIdIntraday,
        brokerTwoIdDelivery,
        brokerTwoIdIntraday,
        brokerThreeIdDelivery,
        brokerThreeIdIntraday,
      } = mergedSettings;

      return {
        id: index + 1,
        _id: item._id,
        scriptName: item.symbolName,
        script_name_value: item.symbol,
        minValue: processValue(minValue),
        maxValue: processValue(maxValue),
        delivery: processValue(delivery),
        intraday: processValue(intraday),
        total: processValue(total),
        type: type || (isEQUOrCRYPTO ? "quantity" : "lot"),
        open: item.open,
        lotSize: item.lotSize,
        brokerOneIdDelivery: brokerOneIdDelivery || null,
        brokerTwoIdDelivery: brokerTwoIdDelivery || null,
        brokerTwoIdIntraday: brokerTwoIdIntraday || null,
        brokerThreeIdDelivery: brokerThreeIdDelivery || null,
        brokerThreeIdIntraday: brokerThreeIdIntraday || null,
        checked: scriptInfo || false,
      };
    });

    return {
      filteredData: transformedData,
      blockedData: transformedBlockedData,
      totalCount: transformedData.length
    };
  }, [allScriptsData, marketWiseAllScript, staticBlocked, scriptSettingsRequest?.blocked, scriptSettingsRequest?.edited, isEQUOrCRYPTO, scriptInfo, searchValue, sort?.column, sort?.order, hasSettingsFromResponse, hasSettingsFromEdited, isBlocked]);

  // Update state when processed data changes
  useEffect(() => {
    setFilteredData(processedData.filteredData);
    setBlockedData(processedData.blockedData);
    setTotalData(processedData.totalCount);

    // Reset to first page when data changes
    setPage(1);

    // Update extra row state
    const globalSettings = scriptSettingsRequest?.edited.find(
      (item) => item.scriptName === "All"
    );

    setExtraRow({
      ...extraRow,
      ...marketWiseAllScript?.["All"],
      ...globalSettings,
      intraday: watchIntraday || initalIntraday,
      delivery: watchDelivery || initalDelivery,
    });
  }, [processedData, marketWiseAllScript, scriptSettingsRequest?.edited, watchIntraday, initalIntraday, watchDelivery, initalDelivery, scriptInfo]);

  // Handle pagination for filtered data
  useEffect(() => {
    if (filteredData.length === 0) {
      setCurrentPageData([]);
      setTotalData(0);
      return;
    }

    // Ensure page is within valid range
    const maxPage = Math.ceil(filteredData.length / pageSize);
    if (page > maxPage && maxPage > 0) {
      setPage(maxPage);
      return;
    }

    // Fix pagination calculation
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filteredData.length);
    const pageData = filteredData.slice(startIndex, endIndex);

    setCurrentPageData(pageData);

    // Set total count for pagination display
    if (totalData !== filteredData.length) {
      setTotalData(filteredData.length);
    }
  }, [filteredData, page, pageSize]);

  // Reset page when search or sort changes
  useEffect(() => {
    setPage(1);
  }, [searchValue, sort?.column, sort?.order]);



  // Remove the scriptInfo useEffect that was interfering with pagination
  // The checked property will be handled in the data transformation instead

  useEffect(() => {
    setExtraRow((pre) => ({
      ...pre,
      delivery: watchDelivery,
      intraday: watchIntraday,
    }));
  }, [watchDelivery, watchIntraday]);

  // useDebounce(
  //   () => {
  //     if (search === "") {
  //       setData([]);
  //       appendData(page);
  //     } else {
  //       const filteredData = valueRef.current?.filter((el) =>
  //         el?.scriptName.toLowerCase().includes?.(search.toLowerCase())
  //       );
  //       setData(filteredData);
  //     }
  //   },
  //   1,
  //   [search]
  // );

  // useEffect(() => {
  //   if (tab) {
  //     setLoading(true);
  //     if (data?.length > 0) setData([]);
  //     dataOffset.current = 0;
  //     console.log("activeKey", activeKey, tab);
  //     activeKey && appendData(page);
  //   }
  // }, [activeKey]);

  // removed select-all mutation; we will compute from allScriptsData when needed

  //function
  // const appendData = async (page, search, sortColumn, sortType) => {
  //   if (!canCallAPI) {
  //     return null;
  //   }
  //   canCallAPI = false;
  //   let dummyData = [];
  //   try {
  //     setLoading(true);
  //     let response = await APICall(
  //       "get",
  //       `${EndPoints.getTableScript}${
  //         SEGMENTS[tab]
  //       }&page=${page}&limit=${10}&search=${search || ""}&sortColumn=${
  //         sortColumn || ""
  //       }&sortType=${sortType || ""}`
  //     );
  //     if (response?.status === 200) {
  //       setData([]);
  //       setPage(Number(response?.data?.data?.currentPage));
  //       setTotalData(response?.data?.data?.totalPages);
  //       dummyData = response?.data?.data?.data;
  //       let count = 1;
  //       /////////////
  //       let draft = [];

  //       let temp = scriptSettingsRequest?.edited.find(
  //         (item) => item.scriptName === "All"
  //       );
  //       for (let index = 0; index < dummyData?.length; index++) {
  //         let minValue =
  //           scripSettings[tab][dummyData[index].symbolName]?.minValue;
  //         let maxValue =
  //           scripSettings[tab][dummyData[index].symbolName]?.maxValue;
  //         let delivery =
  //           scripSettings[tab][dummyData[index].symbolName]?.delivery;
  //         let intraday =
  //           scripSettings[tab][dummyData[index].symbolName]?.intraday;
  //         let total = scripSettings[tab][dummyData[index].symbolName]?.total;
  //         draft.push({
  //           id: count++,
  //           scriptName: dummyData[index].symbolName,
  //           script_name_value: dummyData[index].symbol,
  //           minValue: minValue == 0 ? 0 : Number(minValue) || null,
  //           maxValue: maxValue == 0 ? 0 : Number(maxValue) || null,
  //           delivery: delivery == 0 ? 0 : Number(delivery) || null,
  //           intraday: intraday == 0 ? 0 : Number(intraday) || null,
  //           total: total == 0 ? 0 : Number(total) || null,

  //           type: scripSettings[tab][dummyData[index].symbolName]?.type
  //             ? scripSettings[tab][dummyData[index].symbolName]?.type
  //             : isEQUOrCRYPTO
  //             ? "quantity"
  //             : "lot",

  //           open: dummyData[index]?.open,
  //           lotSize: dummyData[index]?.lotSize,
  //         });
  //       }

  //       setExtraRow({
  //         ...extraRow,
  //         ...scripSettings?.[tab]?.["All"],
  //         ...temp,
  //         intraday: watchIntraday || initalIntraday,
  //         delivery: watchDelivery || initalDelivery,
  //       });

  //       setData([...draft]);
  //     } else {
  //       notification.error({ message: response?.data?.message });
  //     }
  //     // dataOffset.current = dataOffset.current + 5;
  //   } catch (error) {
  //     notification.error({ message: error?.message });
  //   } finally {
  //     setLoading(false);
  //     setTimeout(() => {
  //       canCallAPI = true;
  //     }, 1000);
  //   }

  //   // return console.log("dummyData", dummyData);
  //   // const newData = dummyData.slice(
  //   //   dataOffset.current,
  //   //   dataOffset.current + 5
  //   // );
  // };
  //////////////////////////////////////////////////////////////////////////

  const isEditing = (record) => record.id === editingKey;
  const edit = (record) => {
    form.setFieldsValue({
      ...form.getFieldsValue(),
      ...record,
    });

    setEditingKey(record.id);
  };
  const cancel = () => {
    setEditingKey("");
  };

  const save = async (key) => {
    if (key === "All") {
      let isTouched = form.isFieldsTouched();
      let temp = form.getFieldsValue();
      if (temp?.maxValue === false) {
        temp.maxValue = null;
      }
      if (temp?.minValue === false) {
        temp.minValue = null;
      }
      if (isTouched) {
        // Get existing record to preserve range settings (ranges field)
        const existingRecord = scriptSettingsRequest?.edited?.find(
          (item) => item.scriptName === "All"
        ) || {};

        // Preserve existing ranges field when saving script-wise settings
        // Only include ranges if it was explicitly set in the existing record
        const updatedTemp = { ...temp };
        if (existingRecord.ranges !== undefined && existingRecord.ranges !== null) {
          updatedTemp.ranges = existingRecord.ranges;
        }
        // If ranges was never set (undefined), don't include it - let it stay undefined

        let filtered = scriptSettingsRequest?.edited?.filter(
          (item) => item.scriptName !== "All"
        ) || [];

        dispatch(
          setEditData({
            tab: tab,
            edited: [...filtered, updatedTemp],
          })
        );
      }
      setExtraRow({ ...extraRow, ...temp });
      setEditingKey("");
    } else {
      try {
        const row = await form.validateFields();

        // Get existing record to preserve range settings (ranges field)
        const existingRecord = scriptSettingsRequest?.edited?.find(
          (item) => item.scriptName === row.scriptName
        ) || {};

        let filtered = scriptSettingsRequest?.edited.filter(
          (item) => item.scriptName !== row.scriptName
        );

        // Preserve existing ranges field when saving script-wise settings
        // Only include ranges if it was explicitly set in the existing record
        const updatedRow = { ...row };
        if (existingRecord.ranges !== undefined && existingRecord.ranges !== null) {
          updatedRow.ranges = existingRecord.ranges;
        }
        // If ranges was never set (undefined), don't include it - let it stay undefined

        dispatch(
          setEditData({
            tab: tab,
            edited: [...filtered, updatedRow],
          })
        );

        // Handle existing script editing
        const newData = [...data];
        const index = newData.findIndex((item) => key === item.id);
        if (index > -1) {
          const item = newData[index];
          newData.splice(index, 1, {
            ...item,
            ...row,
          });
          setData(newData);
          setEditingKey("");
        } else {
          newData.push(row);
          setData(newData);
          setEditingKey("");
        }
      } catch (errInfo) {
        console.log("Validate Failed:", errInfo);
      }
    }
  };

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      editable: false,
      width: 60,
    },
    {
      title: "Block",
      dataIndex: "block",
      editable: false,
      width: 60,
      render: (_, record) => {
        const isblocked = staticBlocked?.includes(record?.scriptName);
        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Checkbox
              name={record.scriptName}
              onChange={async (e) => {
                let isChecked = e.target.checked;
                if (record.id === "All") {
                  if (isChecked) {
                    // Select all from current dataset (settings + blocked)
                    const allNames = [
                      ...filteredData.map((item) => item.scriptName),
                      ...blockedData.map((item) => item.scriptName),
                    ];
                    setSelectedRow(["All", ...allNames]);
                  } else {
                    setSelectedRow(staticBlocked);
                  }
                } else {
                  await setSelectedRow((prevSelectedRow) =>
                    isChecked
                      ? [...prevSelectedRow, record.scriptName]
                      : prevSelectedRow.filter(
                        (item) => item !== record.scriptName && item !== "All"
                      )
                  );
                }
              }}
              checked={selectedRow.includes(record.scriptName)}
              disabled={record.id === "All" ? false : isblocked ? true : false}
            />
          </div>
        );
      },
    },

    {
      title: "Script Name(Close)",
      dataIndex: "scriptName",
      width: 200,
      align: "left",
      sorter: true,
      editable: false,
      render: (_, record) => {
        return (
          record.scriptName !== "All" ? (
            <div style={{ textAlign: "left" }}>
              <div>{record.scriptName} ({record.open})</div>
              {record.scriptName !== "" && record.scriptName !== "All" && (
                <div>Lot size : {record.lotSize}</div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: "left" }}>
              {record.scriptName}
            </div>
          )
        );
      },
    },
    {
      title: "Brokerage",
      dataIndex: "brokerage",
      editable: true,
      children: [
        {
          title: "Delivery",
          dataIndex: "delivery",
          editable: false,
          width: 100,
          render: (_, record) => {
            let newBlock = scriptSettingsRequest?.blocked?.includes(
              record?.scriptName
            );
            const isblocked = staticBlocked?.includes(record?.scriptName);

            return (
              <div style={{ textAlign: "center" }}>
                {record?.delivery || "-"}
                {record?.delivery && (
                  <span style={{ marginLeft: "4px", color: "#666", fontSize: "12px" }}>
                    {watchBrokerageType === "percentage" ? "%" : "Fix"}
                  </span>
                )}
              </div>
            );
          },
        },
        {
          title: "Intraday",
          dataIndex: "intraday",
          editable: false,
          width: 100,
          render: (_, record) => {
            let newBlock = scriptSettingsRequest?.blocked?.includes(
              record?.scriptName
            );
            const isblocked = staticBlocked?.includes(record?.scriptName);
            return (
              <div style={{ textAlign: "center" }}>
                {record?.intraday || "-"}
                {record?.intraday && (
                  <span style={{ marginLeft: "4px", color: "#666", fontSize: "12px" }}>
                    {watchBrokerageType === "percentage" ? "%" : "Fix"}
                  </span>
                )}
              </div>
            );
          },
        },
        // Broker 1
        {
          title: "Broker 1 Delivery",
          dataIndex: "brokerOneIdDelivery",
          editable: false,
          width: 100,
          render: (_, record) => (
            <div style={{ textAlign: "center", padding: "4px" }}>
              {record?.brokerOneIdDelivery || "-"}
            </div>
          ),
        },
        {
          title: "Broker 1 Intraday",
          dataIndex: "brokerOneIdIntraday",
          editable: false,
          width: 100,
          render: (_, record) => (
            <div style={{ textAlign: "center", padding: "4px" }}>
              {record?.brokerOneIdIntraday || "-"}
            </div>
          ),
        },
        // Broker 2
        {
          title: "Broker 2 Delivery",
          dataIndex: "brokerTwoIdDelivery",
          editable: false,
          width: 100,
          render: (_, record) => (
            <div style={{ textAlign: "center", padding: "4px" }}>
              {record?.brokerTwoIdDelivery || "-"}
            </div>
          ),
        },
        {
          title: "Broker 2 Intraday",
          dataIndex: "brokerTwoIdIntraday",
          editable: false,
          width: 100,
          render: (_, record) => (
            <div style={{ textAlign: "center", padding: "4px" }}>
              {record?.brokerTwoIdIntraday || "-"}
            </div>
          ),
        },
        // Broker 3
        {
          title: "Broker 3 Delivery",
          dataIndex: "brokerThreeIdDelivery",
          editable: false,
          width: 100,
          render: (_, record) => (
            <div style={{ textAlign: "center", padding: "4px" }}>
              {record?.brokerThreeIdDelivery || "-"}
            </div>
          ),
        },
        {
          title: "Broker 3 Intraday",
          dataIndex: "brokerThreeIdIntraday",
          editable: false,
          width: 100,
          render: (_, record) => (
            <div style={{ textAlign: "center", padding: "4px" }}>
              {record?.brokerThreeIdIntraday || "-"}
            </div>
          ),
        },
      ],
    },
    {
      title: "L / Q / V",
      dataIndex: "type",
      editable: false,
      width: 100,
      render: (_, record) => {
        let newBlock = scriptSettingsRequest?.blocked?.includes(
          record?.scriptName
        );
        const isblocked = staticBlocked?.includes(record?.scriptName);
        let defalutvalue = isEQUOrCRYPTO ? "quantity" : "lot";

        return (
          <div style={{ textAlign: "center" }}>
            {record?.type || defalutvalue}
          </div>
        );
      },
    },
    {
      title: "Single Trade",
      dataIndex: "singleTrade",
      width: 200,
      editable: false,

      children: [
        {
          title: "Min",
          dataIndex: "minValue",
          width: 100,
          editable: false,
          render: (_, record) => {
            const isblocked = staticBlocked?.includes(record?.scriptName);
            let newBlock = scriptSettingsRequest?.blocked?.includes(
              record?.scriptName
            );

            return (
              <div style={{ textAlign: "center" }}>
                {record?.minValue || "-"}
              </div>
            );
          },
        },
        {
          title: "Max",
          dataIndex: "maxValue",
          width: 100,
          editable: false,
          render: (_, record) => {
            const isblocked = staticBlocked?.includes(record?.scriptName);
            let newBlock = scriptSettingsRequest?.blocked?.includes(
              record?.scriptName
            );
            return (
              <div style={{ textAlign: "center" }}>
                {record?.maxValue || "-"}
              </div>
            );
          },
        },
      ],
    },
    {
      title: "Total",
      dataIndex: "total",
      editable: false,
      width: 100,
      render: (_, record) => {
        const isblocked = staticBlocked?.includes(record?.scriptName);
        let newBlock = scriptSettingsRequest?.blocked?.includes(
          record?.scriptName
        );
        return (
          <div style={{ textAlign: "center" }}>
            {record?.total || "-"}
          </div>
        );
      },
      // validatorFunction: (a, b) => {
      //   if (
      //     isRent
      //       ? b < 0
      //       : b > parentScriptS?.marginDetails?.[tab].delivery || b < 0
      //   ) {
      //     return Promise.reject(
      //       new Error(
      //         ` You can not give total less then  ${
      //           isRent ? "0" : parentScriptS?.marginDetails?.[tab].delivery
      //         }`
      //       )
      //     );
      //   } else {
      //     return Promise.resolve();
      //   }
      // },
    },
    {
      title: "operation",
      dataIndex: "operation",
      width: 120,
      // fixed: "right",
      editable: false,
      render: (_, record) => {
        const editable = isEditing(record);

        return (
          <div style={{ width: "100px" }}>
            {editable ? (
              <>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => save(record.id)}
                  icon={<SaveOutlined />}
                  style={{
                    marginRight: 8,
                  }}
                ></Button>
                {/* <Popconfirm
                  title="Sure to cancel?"
                  placement="topRight"
                  onConfirm={cancel}
                >
                  <Button size="small" danger icon={<StopOutlined />}></Button>
                </Popconfirm> */}
              </>
            ) : (
              <>
                <Button
                  type="primary"
                  size="small"
                  disabled={
                    accountType !== "customer" ||
                      selectedRow.includes(record.scriptName) ||
                      editingKey !== "" ||
                      staticBlocked?.[tab]?.includes(`${record.scriptName}`)
                      ? true
                      : false
                  }
                  onClick={() => {
                    setEditScriptData(record);
                    setShowScriptModal(true);
                  }}
                  icon={<EditOutlined />}
                  style={{ marginRight: 8 }}
                ></Button>
                <Button
                  title="Edit"
                  type="primary"
                  style={{ visibility: "hidden" }}
                  size="small"
                  disabled={editingKey !== ""}
                  onClick={() => edit(record)}
                  icon={<EditOutlined />}
                ></Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const otherColumns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      editable: false,
      width: 60,
    },
    {
      title: "Block",
      dataIndex: "block",
      width: 60,
      editable: false,
      render: (_, record) => {
        const isblocked = staticBlocked?.includes(record?.scriptName);

        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Checkbox
              name={record.scriptName}
              onChange={(e) => {
                let isChecked = e.target.checked;
                if (record.id === "All") {
                  if (isChecked) {
                    // Select all from current dataset (settings + blocked)
                    const allNames = [
                      ...filteredData.map((item) => item.scriptName),
                      ...blockedData.map((item) => item.scriptName),
                    ];
                    setSelectedRow(["All", ...allNames]);
                  } else {
                    // let keys = [];
                    // for (let key in scripSettings[tab]) {
                    //   let isParentBlocked =
                    //     parentScriptS?.blockScript?.[tab]?.includes(key);
                    //   isblocked && keys.push(key);
                    // }
                    setSelectedRow(staticBlocked);
                  }
                } else {
                  if (isChecked) {
                    setSelectedRow([...selectedRow, record.scriptName]);
                  } else {
                    setSelectedRow(
                      selectedRow.filter(
                        (item) => item !== record.scriptName && item !== "All"
                      )
                    );
                  }
                }
              }}
              checked={selectedRow.includes(record.scriptName)}
              disabled={record.id === "All" ? false : isblocked ? true : false}
            />
          </div>
        );
      },
    },
    {
      title: "Script Name",
      dataIndex: "scriptName",
      width: 200,
      sorter: true,
      editable: false,
      render: (_, record) => {
        return (
          <div>
            <div>{record.scriptName}</div>
            {record.scriptName !== "All" && (
              <div>Lot size : {record.lotSize}</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Close Price",
      dataIndex: "open",
      sorter: true,
      editable: false,
      width: 100,
    },
  ];

  useEffect(() => {
    // set inital block from ban script api
    if (scriptSettingsRequest.blocked) {
      let blocked = scriptSettingsRequest.blocked;
      setSelectedRow(blocked);
    }
  }, []);

  useEffect(() => {
    if (editingKey === "") {
      form.resetFields();
    }
  }, [editingKey]);

  function appendDataByKey(targetArray, sourceArray, key) {
    // Create a map from the source array for quick lookups
    const sourceMap = new Map(sourceArray.map((item) => [item[key], item]));

    // Append data from the source map to the target array
    return targetArray.map((item) => {
      const sourceItem = sourceMap.get(item[key]);
      if (sourceItem) {
        return { ...item, ...sourceItem };
      }
      return item;
    });
  }



  useEffect(() => {
    if (scriptSettingsRequest?.edited?.length > 0 && currentPageData && !allScriptsLoading) {
      let tempData = appendDataByKey(
        currentPageData,
        scriptSettingsRequest?.edited,
        "scriptName"
      );
      setCurrentPageData([...tempData]);
    }
  }, [scriptSettingsRequest]);

  useEffect(() => {
    dispatch(setBlockedScripts({ tab: tab, blocked: selectedRow }));
  }, [selectedRow]);

  useEffect(() => {
    dispatch(setDeletedScripts({ tab: tab, deleted: deletedScriptsList }));
  }, [deletedScriptsList, tab, dispatch]);

  // useEffect(() => {
  //   return () => {
  //     setSelectedRow([]);
  //   };
  // }, [accountType]);
  // useEffect(() => {
  //   if (parentScriptS?.blockScript?.[tab]?.length > 0) {
  //     setSelectedRow((pre) => [...pre, ...parentScriptS?.blockScript?.[tab]]);
  //   }
  //   console.log(
  //     "parentScriptS?.blockScript?.[tab]",
  //     parentScriptS?.blockScript?.[tab]
  //   );
  // }, []);

  const handleSearch = (e) => {
    functionDebounce(e);
  };

  const functionDebounce = _.debounce((e) => {
    if (page !== 1) {
      setPage(1);
    }
    setSearchValue(e.target.value);
  }, 300);

  return (
    <>


      {/* Blocked Scripts Section */}
      {blockedData.length > 0 && (
        <div style={{
          marginBottom: "2rem",
          border: "1px solid #ff4d4f",
          borderRadius: "8px",
          padding: isMobile ? "12px" : "16px"
        }}>
          <div style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isMobile ? "flex-start" : "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            marginBottom: "12px",
            gap: isMobile ? "8px" : "0"
          }}>
            <h4 style={{
              color: "#ff4d4f",
              margin: 0,
              fontSize: isMobile ? "16px" : "18px"
            }}>
              Blocked Scripts ({blockedData.length})
            </h4>
            <span style={{
              color: "#666",
              fontSize: isMobile ? "11px" : "12px"
            }}>
              Click to unblock scripts
            </span>
          </div>
          <div
            className="block-scripts-scrollable"
            style={{
              maxHeight: isMobile ? "200px" : "300px",
              overflowY: "auto",
              paddingRight: "8px"
            }}
          >
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(auto-fill, minmax(120px, 1fr))" : "repeat(auto-fill, minmax(200px, 1fr))",
              gap: isMobile ? "4px" : "8px"
            }}>
              {blockedData.map((script) => (
                <div
                  key={script.scriptName}
                  style={{
                    backgroundColor: "#fff2f0",
                    border: "1px solid #ffccc7",
                    borderRadius: "6px",
                    padding: isMobile ? "4px 6px" : "8px 12px",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: isMobile ? "2px" : "8px",
                    transition: "all 0.2s",
                    textAlign: isMobile ? "center" : "left",
                  }}
                  onClick={() => {
                    // Remove from blocked list
                    const newBlockedData = blockedData.filter(item => item.scriptName !== script.scriptName);
                    setBlockedData(newBlockedData);

                    // Remove from selectedRow if present
                    setSelectedRow(prev => prev.filter(item => item !== script.scriptName));

                    // processedData shows a scriptwise card only if the script has settings in initial/response data (marketWiseAllScript).
                    // Unblocking does not add a card for scripts that had no scriptwise settings in the response.
                  }}

                >
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isMobile ? "center" : "flex-start",
                    flex: 1
                  }}>
                    <span style={{
                      color: "#ff4d4f",
                      fontWeight: "500",
                      fontSize: isMobile ? "10px" : "12px"
                    }}>
                      {script.scriptName}
                    </span>
                    {!isMobile && (
                      <span style={{
                        color: "#666",
                        fontSize: "12px"
                      }}>
                        ({script.open})
                      </span>
                    )}
                  </div>
                  <span style={{
                    color: "#ff4d4f",
                    fontSize: isMobile ? "9px" : "12px"
                  }}>
                    ✕
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile-responsive header section */}
      <div style={{ marginBottom: "1rem" }}>


        {/* Responsive controls container */}
        <div className="footer-controls-container">
          {/* Search field - prioritized on mobile */}
          <div className="search-container" style={{
            width: "100%",
            maxWidth: isMobile ? "100%" : "300px"
          }}>
            <Input
              style={{ width: "100%" }}
              onChange={handleSearch}
              placeholder={isMobile ? "Search..." : "Search scripts..."}
              prefix={<SearchOutlined />}
              size={isMobile ? "small" : "middle"}
            />
          </div>



          {/* Action buttons container */}
          <div className="action-buttons-container">
            {/* Block Scripts button */}
            <Button
              size={isMobile ? "small" : "middle"}
              danger
              type="primary"
              onClick={() => {
                setShowBlockScriptModal(true);
              }}
              icon={<StopOutlined />}
              style={{
                fontWeight: "500",
                borderRadius: "6px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                width: "100%",
                fontSize: isMobile ? "11px" : "14px"
              }}
            >
              {isMobile ? "Block" : "Block Scripts"}
            </Button>

            {/* Add script dropdown */}
            <Select
              style={{ width: "100%" }}
              placeholder={isMobile ? "Add script..." : "Add script to settings..."}
              showSearch
              size={isMobile ? "small" : "middle"}
              value={selectedScriptValue}
              suffixIcon={<EditOutlined style={{ color: "#52c41a" }} />}
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value) => {
                setSelectedScriptValue(value);
                const selectedScript = availableScripts.find(script => script.symbolName === value);
                if (selectedScript) {
                  // Create the script data for settings
                  const newScriptWithSettings = {
                    id: filteredData.length + 1,
                    scriptName: selectedScript.symbolName,
                    script_name_value: selectedScript.symbol,
                    minValue: null,
                    maxValue: null,
                    delivery: null,
                    intraday: null,
                    total: null,
                    type: isEQUOrCRYPTO ? "quantity" : "lot",
                    open: selectedScript.open,
                    lotSize: selectedScript.lotSize,
                    brokerOneIdDelivery: null,
                    brokerOneIdIntraday: null,
                    brokerTwoIdDelivery: null,
                    brokerTwoIdIntraday: null,
                    brokerThreeIdDelivery: null,
                    brokerThreeIdIntraday: null,
                  };

                  // Set the edit data and open settings modal directly
                  setEditScriptData(newScriptWithSettings);
                  setIsAddingNewScript(true);
                  setShowScriptModal(true);
                  // Don't add to filtered data yet - wait for form submission
                }
              }}
              options={availableScripts.map(script => ({
                value: script.symbolName,
                label: isMobile ? script.symbolName : `${script.symbolName} (${script.open})`,
              }))}
            />
          </div>
        </div>
      </div>

      <Form form={form} component={false}>
        {allScriptsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            Loading...
          </div>
        ) : (
          <>
            <div className="masonry-container">
              {/* All Row Card */}
              {accountType === "customer" && (
                <div
                  className={`masonry-card all-row`}
                  onClick={(e) => {
                    if (e.target.closest("button")) {
                      return;
                    }
                    setEditScriptData(extraRow);
                    setShowScriptModal(true);
                  }}
                >
                  <div className="masonry-card-header">
                    <div className="masonry-card-title all-title">
                      {extraRow.scriptName}
                    </div>
                    <div className="masonry-card-header-buttons">
                      <Button
                        size="small"
                        danger={true}
                        type={
                          selectedRow.includes(extraRow.scriptName)
                            ? "primary"
                            : "default"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedRow.includes(extraRow.scriptName)) {
                            setSelectedRow(staticBlocked);
                          } else {
                            const allNames = [
                              ...filteredData.map((item) => item.scriptName),
                              ...blockedData.map((item) => item.scriptName),
                            ];
                            setSelectedRow(["All", ...allNames]);
                          }
                        }}
                        style={{
                          height: "20px",
                          fontSize: "9px",
                          padding: "0 6px",
                          color: "white",
                          backgroundColor: "red",
                          lineHeight: "1",
                        }}
                      >
                        {selectedRow.includes(extraRow.scriptName)
                          ? "Unblock All"
                          : "Block All"}
                      </Button>
                    </div>
                  </div>
                  <div className="masonry-card-body">
                    <div className="masonry-card-section">
                      <div className="masonry-card-section-title">Brokerage</div>
                      <div className="masonry-card-brokerage-grid">
                        <div className="masonry-card-row">
                          <span className="masonry-card-label">Delivery</span>
                          <span className="masonry-card-value">
                            {extraRow.delivery || "-"}
                            {extraRow.delivery && (
                              <span style={{ marginLeft: "2px", color: "#666", fontSize: "9px" }}>
                                {watchBrokerageType === "percentage" ? "%" : "Fix"}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="masonry-card-row">
                          <span className="masonry-card-label">Intraday</span>
                          <span className="masonry-card-value">
                            {extraRow.intraday || "-"}
                            {extraRow.intraday && (
                              <span style={{ marginLeft: "2px", color: "#666", fontSize: "9px" }}>
                                {watchBrokerageType === "percentage" ? "%" : "Fix"}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                      {(extraRow.brokerOneIdDelivery || extraRow.brokerOneIdIntraday ||
                        extraRow.brokerTwoIdDelivery || extraRow.brokerTwoIdIntraday ||
                        extraRow.brokerThreeIdDelivery || extraRow.brokerThreeIdIntraday) && (
                          <div className="masonry-card-broker-grid">
                            {extraRow.brokerOneIdDelivery && (
                              <div className="masonry-card-row">
                                <span className="masonry-card-label">B1 Del</span>
                                <span className="masonry-card-value">{extraRow.brokerOneIdDelivery}{watchBrokerageType === "percentage" ? "%" : "Fix"}</span>

                              </div>
                            )}
                            {extraRow.brokerOneIdIntraday && (
                              <div className="masonry-card-row">
                                <span className="masonry-card-label">B1 Intra</span>
                                <span className="masonry-card-value">{extraRow.brokerOneIdIntraday}{watchBrokerageType === "percentage" ? "%" : "Fix"}</span>
                              </div>
                            )}
                            {extraRow.brokerTwoIdDelivery && (
                              <div className="masonry-card-row">
                                <span className="masonry-card-label">B2 Del</span>
                                <span className="masonry-card-value">{extraRow.brokerTwoIdDelivery}{watchBrokerageType === "percentage" ? "%" : "Fix"}</span>
                              </div>
                            )}
                            {extraRow.brokerTwoIdIntraday && (
                              <div className="masonry-card-row">
                                <span className="masonry-card-label">B2 Intra</span>
                                <span className="masonry-card-value">{extraRow.brokerTwoIdIntraday}{watchBrokerageType === "percentage" ? "%" : "Fix"}</span>
                              </div>
                            )}
                            {extraRow.brokerThreeIdDelivery && (
                              <div className="masonry-card-row">
                                <span className="masonry-card-label">B3 Del</span>
                                <span className="masonry-card-value">{extraRow.brokerThreeIdDelivery}{watchBrokerageType === "percentage" ? "%" : "Fix"}</span>
                              </div>
                            )}
                            {extraRow.brokerThreeIdIntraday && (
                              <div className="masonry-card-row">
                                <span className="masonry-card-label">B3 Intra</span>
                                <span className="masonry-card-value">{extraRow.brokerThreeIdIntraday}{watchBrokerageType === "percentage" ? "%" : "Fix"}</span>
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                    <div className="masonry-card-section">
                      <div className="masonry-card-row">
                        <span className="masonry-card-section-title">Quaantity settings</span>
                      </div>


                      <div className="masonry-card-grid">
                        <div className="masonry-card-row">
                          <span className="masonry-card-label">Min {extraRow.type || isEQUOrCRYPTO ? "quantity" : "lot"}</span>
                          <span className="masonry-card-value">{extraRow.minValue || "-"}</span>
                        </div>
                        <div className="masonry-card-row">
                          <span className="masonry-card-label">Max {extraRow.type || isEQUOrCRYPTO ? "quantity" : "lot"}</span>
                          <span className="masonry-card-value">{extraRow.maxValue || "-"}</span>
                        </div>
                        <div className="masonry-card-row">
                          <span className="masonry-card-label">Position Total {extraRow.type || isEQUOrCRYPTO ? "quantity" : "lot"}</span>
                          <span className="masonry-card-value">{extraRow.total || "-"}</span>
                        </div>
                      </div>
                    </div>
                    {(() => {
                      const isAllRow = extraRow.scriptName === "All";
                      if (!isAllRow || accountType !== "customer") return null;

                      // Check if ranges actually exists (not from script-wise settings)
                      const hasExistingRanges = extraRow.ranges &&
                        Array.isArray(extraRow.ranges) &&
                        extraRow.ranges.length > 0;

                      // Only use actual ranges, don't create from script-wise settings
                      const ranges = hasExistingRanges ? extraRow.ranges : [];

                      return (
                        <div className="masonry-card-section">
                          <div className="masonry-card-section-title">
                            Range Settings
                            <Button
                              size="small"
                              type="link"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Only use actual ranges, don't create from script-wise settings
                                const ranges = extraRow.ranges || [];
                                setRangeSettingsData({
                                  scriptName: extraRow.scriptName || "All",
                                  id: "All",
                                  ranges: ranges,
                                });
                                setShowRangeSettingsModal(true);
                              }}
                              icon={<EditOutlined />}
                              style={{ padding: 0, fontSize: "10px", height: "20px", border: "none" }}
                            >
                              {hasExistingRanges ? "Edit" : "Add"}
                            </Button>
                          </div>
                          {hasExistingRanges && (
                            <div style={{ marginTop: 4 }}>
                              {ranges.map((range, idx) => {
                                const typeLabel = getTypeLabel(
                                  range.type || (isEQUOrCRYPTO ? "quantity" : "lot")
                                );
                                const minDisplay =
                                  (range.minValue ?? "") !== ""
                                    ? range.minValue + " " + typeLabel
                                    : "-";
                                const maxDisplay =
                                  (range.maxValue ?? "") !== ""
                                    ? range.maxValue + " " + typeLabel
                                    : "-";
                                const totalDisplay =
                                  (range.total ?? "") !== ""
                                    ? range.total + " " + typeLabel
                                    : "-";

                                return (
                                  <div
                                    key={idx}
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "60px 1fr 1fr 1fr",
                                      gap: "0 12px",
                                      alignItems: "center",
                                      marginBottom: 4,
                                    }}
                                  >
                                    <span className="masonry-card-label">
                                      {(range.minBoundary ?? "") !== ""
                                        ? range.minBoundary
                                        : 0}
                                      -
                                      {(range.maxBoundary ?? "") !== ""
                                        ? range.maxBoundary
                                        : "∞"}
                                    </span>
                                    <span className="masonry-card-label">
                                      min:{" "}
                                      <span className="masonry-card-value">
                                        {minDisplay}
                                      </span>
                                    </span>
                                    <span className="masonry-card-label">
                                      max:{" "}
                                      <span className="masonry-card-value">
                                        {maxDisplay}
                                      </span>
                                    </span>
                                    <span className="masonry-card-label">
                                      total:{" "}
                                      <span className="masonry-card-value">
                                        {totalDisplay}
                                      </span>
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Script Cards */}
              {currentPageData.map((record) => {
                const isblocked = staticBlocked?.includes(record?.scriptName);
                const editable = isEditing(record);
                const defalutvalue = isEQUOrCRYPTO ? "quantity" : "lot";

                const recordId = record._id || record.scriptName;
                const isDeleted = recordId ? deletedScriptsList.includes(recordId) : false;

                return (
                  <div
                    key={record.scriptName || record.id}
                    className={`masonry-card ${isDeleted ? 'deleted' : ''}`}
                    onClick={(e) => {
                      if (e.target.closest('button')) {
                        return;
                      }
                      setEditScriptData(record);
                      setShowScriptModal(true);
                    }}
                  >
                    {isDeleted && (
                      <div className="masonry-card-deleted-overlay">
                        <div className="masonry-card-deleted-text">DELETED</div>
                      </div>
                    )}
                    <div className="masonry-card-header">
                      <div className="masonry-card-title">

                        {record.scriptName !== "All" ? (
                          <div style={{ lineHeight: 1.2 }}>
                            <div style={{ fontSize: "12px" }}>{record.id}. {record.scriptName}({record.open})</div>
                            {record.lotSize && (
                              <div style={{ fontSize: "9px", color: "#8c8c8c", fontWeight: "normal" }}>
                                Lot size : {record.lotSize}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>{record.scriptName}</div>
                        )}
                      </div>
                      <div className="masonry-card-header-buttons" style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                        <Button
                          size="small"
                          danger={selectedRow.includes(record.scriptName)}
                          type={selectedRow.includes(record.scriptName) ? "primary" : "default"}
                          disabled={isblocked}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRow((prevSelectedRow) =>
                              selectedRow.includes(record.scriptName)
                                ? prevSelectedRow.filter(
                                  (item) => item !== record.scriptName && item !== "All"
                                )
                                : [...prevSelectedRow, record.scriptName]
                            );
                          }}
                          style={{ height: "20px", fontSize: "9px", padding: "0 6px", color: "white", backgroundColor: "red", lineHeight: "1" }}
                        >
                          {selectedRow.includes(record.scriptName) ? "Unblock" : "Block"}
                        </Button>
                        <Button
                          size="small"
                          danger
                          type="default"
                          icon={<DeleteOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            const recordId = record._id || record.scriptName;
                            if (recordId && !deletedScriptsList.includes(recordId)) {
                              setDeletedScriptsList((prev) => [...prev, recordId]);
                            }
                          }}
                          style={{ height: "20px", fontSize: "9px", padding: "0 6px", lineHeight: "1" }}
                        >

                        </Button>
                      </div>
                    </div>
                    <div className="masonry-card-body">
                      {userAccountType === "customer" ? (
                        <>
                          <div className="masonry-card-section">
                            <div className="masonry-card-section-title">Brokerage</div>
                            <div className="masonry-card-brokerage-grid">
                              <div className="masonry-card-row">
                                <span className="masonry-card-label">Delivery</span>
                                <span className="masonry-card-value">
                                  {record.delivery || "-"}
                                  {record.delivery && (
                                    <span style={{ marginLeft: "2px", color: "#666", fontSize: "9px" }}>
                                      {watchBrokerageType === "percentage" ? "%" : "Fix"}
                                    </span>
                                  )}
                                </span>
                              </div>
                              <div className="masonry-card-row">
                                <span className="masonry-card-label">Intraday</span>
                                <span className="masonry-card-value">
                                  {record.intraday || "-"}
                                  {record.intraday && (
                                    <span style={{ marginLeft: "2px", color: "#666", fontSize: "9px" }}>
                                      {watchBrokerageType === "percentage" ? "%" : "Fix"}
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                            {(record.brokerOneIdDelivery || record.brokerOneIdIntraday ||
                              record.brokerTwoIdDelivery || record.brokerTwoIdIntraday ||
                              record.brokerThreeIdDelivery || record.brokerThreeIdIntraday) && (
                                <div className="masonry-card-broker-grid">
                                  {record.brokerOneIdDelivery && (
                                    <div className="masonry-card-row">
                                      <span className="masonry-card-label">B1 Del</span>
                                      <span className="masonry-card-value">{record.brokerOneIdDelivery}</span>
                                    </div>
                                  )}
                                  {record.brokerOneIdIntraday && (
                                    <div className="masonry-card-row">
                                      <span className="masonry-card-label">B1 Intra</span>
                                      <span className="masonry-card-value">{record.brokerOneIdIntraday}</span>
                                    </div>
                                  )}
                                  {record.brokerTwoIdDelivery && (
                                    <div className="masonry-card-row">
                                      <span className="masonry-card-label">B2 Del</span>
                                      <span className="masonry-card-value">{record.brokerTwoIdDelivery}</span>
                                    </div>
                                  )}
                                  {record.brokerTwoIdIntraday && (
                                    <div className="masonry-card-row">
                                      <span className="masonry-card-label">B2 Intra</span>
                                      <span className="masonry-card-value">{record.brokerTwoIdIntraday}</span>
                                    </div>
                                  )}
                                  {record.brokerThreeIdDelivery && (
                                    <div className="masonry-card-row">
                                      <span className="masonry-card-label">B3 Del</span>
                                      <span className="masonry-card-value">{record.brokerThreeIdDelivery}</span>
                                    </div>
                                  )}
                                  {record.brokerThreeIdIntraday && (
                                    <div className="masonry-card-row">
                                      <span className="masonry-card-label">B3 Intra</span>
                                      <span className="masonry-card-value">{record.brokerThreeIdIntraday}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                          <div className="masonry-card-section">
                            <div className="masonry-card-row">
                              <span className="masonry-card-section-title">Quaantity settings</span>
                            </div>


                            <div className="masonry-card-grid">
                              <div className="masonry-card-row">
                                <span className="masonry-card-label">Min {record.type || defalutvalue}</span>
                                <span className="masonry-card-value">{record.minValue || "-"}</span>
                              </div>
                              <div className="masonry-card-row">
                                <span className="masonry-card-label">Max {record.type || defalutvalue}</span>
                                <span className="masonry-card-value">{record.maxValue || "-"}</span>
                              </div>
                              <div className="masonry-card-row">
                                <span className="masonry-card-label">Position Total {record.type || defalutvalue}</span>
                                <span className="masonry-card-value">{record.total || "-"}</span>
                              </div>
                            </div>
                          </div>

                        </>
                      ) : (
                        <>
                          <div className="masonry-card-section">
                            <div className="masonry-card-row">
                              <span className="masonry-card-label">Close Price</span>
                              <span className="masonry-card-value">{record.open || "-"}</span>
                            </div>
                          </div>
                        </>
                      )}
                      {editable && (
                        <div className="masonry-card-action">
                          <Button
                            type="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              save(record.id);
                            }}
                            icon={<SaveOutlined />}
                            style={{ height: "20px", fontSize: "9px", padding: "0 6px", lineHeight: "1" }}
                          >
                            Save
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <Pagination
                total={totalData}
                current={page}
                pageSize={pageSize}
                showSizeChanger
                pageSizeOptions={[10, 20, 50]}
                showQuickJumper
                onChange={(p) => setPage(p)}
                onShowSizeChange={(_, ps) => {
                  setPageSize(ps);
                  setPage(1);
                }}
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
              />
            </div>
          </>
        )}
      </Form>
      {showScriptModal && (
        <ScriptSettingsForm
          setShowModal={setShowScriptModal}
          tab={tab}
          editData={editScriptData}
          parentForm={parentForm}
          brokarageDetails={brokarageDetails}
          isRent={isRent}
          watchBrokerageType={watchBrokerageType}
          onFormSubmit={(formData) => {
            if (isAddingNewScript) {
              // The useEffect will automatically process the new data from Redux
              setIsAddingNewScript(false);
              setShowScriptModal(false);
              setSelectedScriptValue(null); // Clear dropdown selection after successful submission
            }
          }}
          isAddingNewScript={isAddingNewScript}
        />
      )}

      {/* Block Scripts Modal */}
      {showBlockScriptModal && (
        <Modal
          title="Block Scripts"
          open={showBlockScriptModal}
          onCancel={() => {
            setShowBlockScriptModal(false);
            setSearchValue(""); // Clear search when modal is closed
          }}
          footer={null}
          width={isMobile ? "95%" : 800}
          style={{ top: isMobile ? 20 : 100 }}
        >
          <div style={{ marginBottom: "16px" }}>
            <p style={{ color: "#666", marginBottom: "12px" }}>
              Select scripts to block. Click on a script to block it instantly.
            </p>

            {/* Search Bar */}
            <div style={{ marginBottom: "12px" }}>
              <Input
                placeholder="Search scripts to block..."
                prefix={<SearchOutlined />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                size={isMobile ? "small" : "middle"}
                style={{ width: "100%" }}
              />
            </div>
          </div>

          {
            <div
              className="block-scripts-scrollable"
              style={{
                maxHeight: isMobile ? "250px" : "400px",
                overflowY: "auto",
                overflowX: "hidden",
                paddingRight: "8px"
              }}
            >
              <div style={{
                marginBottom: "8px",
                color: "#666",
                fontSize: isMobile ? "11px" : "12px"
              }}>
                Available scripts to block: {unblockedScripts.filter(script =>
                  script.symbolName.toLowerCase().includes(searchValue.toLowerCase())
                ).length}
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "repeat(auto-fill, minmax(110px, 1fr))" : "repeat(auto-fill, minmax(200px, 1fr))",
                gap: isMobile ? "4px" : "8px"
              }}>
                {unblockedScripts
                  .filter(script =>
                    script.symbolName.toLowerCase().includes(searchValue.toLowerCase())
                  )
                  .length === 0 ? (
                  <div style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#999",
                    fontSize: isMobile ? "12px" : "14px"
                  }}>
                    {searchValue ? "No scripts found matching your search." : "No scripts available to block."}
                  </div>
                ) : (
                  unblockedScripts
                    .filter(script =>
                      script.symbolName.toLowerCase().includes(searchValue.toLowerCase())
                    )
                    .map((script) => (
                      <div
                        key={script.symbolName}
                        style={{
                          padding: isMobile ? "4px 6px" : "12px",
                          border: "1px solid #d9d9d9",
                          borderRadius: "6px",
                          cursor: "pointer",
                          backgroundColor: "#fafafa",
                          transition: "all 0.2s",
                          textAlign: isMobile ? "center" : "left",
                        }}
                        onClick={() => {
                          addScriptToBlocked(script);
                          // Remove from unblocked list
                          setUnblockedScripts(prev => prev.filter(s => s.symbolName !== script.symbolName));
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#ffccc7";
                          e.target.style.borderColor = "#ff4d4f";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#fafafa";
                          e.target.style.borderColor = "#d9d9d9";
                        }}
                      >
                        <div style={{
                          fontWeight: "500",
                          color: "#ff4d4f",
                          fontSize: isMobile ? "10px" : "12px"
                        }}>
                          {script.symbolName}
                        </div>
                        {!isMobile && (
                          <div style={{
                            fontSize: "12px",
                            color: "#666"
                          }}>
                            Close: {script.open}
                          </div>
                        )}
                        <div style={{
                          fontSize: isMobile ? "8px" : "11px",
                          color: "#999"
                        }}>
                          Lot: {script.lotSize}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          }

          {unblockedScripts.length === 0 && (
            <div style={{
              textAlign: "center",
              color: "#666",
              padding: isMobile ? "16px" : "20px",
              fontSize: isMobile ? "12px" : "14px"
            }}>
              All scripts are already blocked.
            </div>
          )}
        </Modal>
      )}

      {/* Range Settings Modal */}
      {showRangeSettingsModal && rangeSettingsData && (
        <RangeSettingsModal
          visible={showRangeSettingsModal}
          onCancel={() => {
            setShowRangeSettingsModal(false);
            setRangeSettingsData(null);
          }}
          onSave={(ranges) => {
            // Get existing record to preserve script-wise settings (minValue, maxValue, total, type)
            const existingRecord = scriptSettingsRequest?.edited?.find(
              (item) => item.scriptName === rangeSettingsData.scriptName
            ) || {};

            // Update the record with new ranges, but preserve existing script-wise settings
            // Range settings and script-wise settings are separate - don't overwrite script-wise settings
            const updatedRecord = {
              ...rangeSettingsData,
              ...existingRecord, // Preserve existing script-wise settings
              ranges: ranges, // Only update ranges
              // Do NOT overwrite minValue, maxValue, total, type from ranges
              // These are set separately in the Edit Script Settings modal
            };

            // Update Redux store
            let filtered = scriptSettingsRequest?.edited?.filter(
              (item) => item.scriptName !== rangeSettingsData.scriptName
            ) || [];

            dispatch(
              setEditData({
                tab: tab,
                edited: [...filtered, updatedRecord],
              })
            );

            setShowRangeSettingsModal(false);
            setRangeSettingsData(null);
          }}
          initialRanges={rangeSettingsData.ranges || []}
          isEQUOrCRYPTO={isEQUOrCRYPTO}
          isMobile={isMobile}
        />
      )}

    </>
  );
};

// Range Settings Modal Component
const RangeSettingsModal = ({
  visible,
  onCancel,
  onSave,
  initialRanges,
  isEQUOrCRYPTO,
  isMobile,
}) => {
  const [form] = Form.useForm();
  const [ranges, setRanges] = useState(initialRanges.length > 0 ? initialRanges : [{
    minBoundary: 0,
    maxBoundary: null,
    minValue: null,
    maxValue: null,
    total: null,
    type: isEQUOrCRYPTO ? "quantity" : "lot",
  }]);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({ ranges: ranges });
    }
  }, [visible, ranges, form]);

  const validateMinValue = (index) => ({ getFieldValue }) => ({
    validator(_, value) {
      const ranges = getFieldValue('ranges') || [];
      const currentRange = ranges[index];
      const maxValue = currentRange?.maxValue;
      if (value === undefined || value === null) return Promise.resolve();
      if (maxValue && value > maxValue) {
        return Promise.reject(new Error('Min value must be less than or equal to Max value'));
      }
      if (value <= 0) {
        return Promise.reject(new Error('Min value must be greater than 0'));
      }
      return Promise.resolve();
    }
  });

  const validateMaxValue = (index) => ({ getFieldValue }) => ({
    validator(_, value) {
      const ranges = getFieldValue('ranges') || [];
      const currentRange = ranges[index];
      const minValue = currentRange?.minValue;
      if (value === undefined || value === null) return Promise.resolve();
      if (minValue !== undefined && value < minValue) {
        return Promise.reject(new Error('Max value must be greater than or equal to Min value'));
      }
      if (value < 0) {
        return Promise.reject(new Error('Max value must be greater than or equal to 0'));
      }
      return Promise.resolve();
    }
  });

  const validateRangeValues = (index) => ({ getFieldValue }) => ({
    validator() {
      const ranges = getFieldValue('ranges') || [];
      const currentRange = ranges[index] || {};
      const minValue = currentRange?.minValue;
      const maxValue = currentRange?.maxValue;
      const total = currentRange?.total;

      const isEmpty = (v) => v === undefined || v === null || v === '';

      // At least one of Min, Max or Total must be filled
      if (isEmpty(minValue) && isEmpty(maxValue) && isEmpty(total)) {
        return Promise.reject(
          new Error('At least one of Min, Max or Total is required.')
        );
      }

      return Promise.resolve();
    },
  });

  const validateTotal = (index) => ({ getFieldValue }) => ({
    validator(_, value) {
      const isEmpty = (v) => v === undefined || v === null || v === '';

      if (!isEmpty(value) && typeof value === 'number' && value < 0) {
        return Promise.reject(
          new Error('Total must be greater than or equal to 0')
        );
      }

      return Promise.resolve();
    },
  });

  const checkRangeOverlap = (ranges, currentIndex, minBoundary, maxBoundary) => {
    for (let i = 0; i < ranges.length; i++) {
      if (i === currentIndex) continue;
      const otherRange = ranges[i];
      if (otherRange?.minBoundary != null && otherRange?.maxBoundary != null) {
        if (minBoundary != null && maxBoundary != null) {
          // Check if ranges overlap
          if (
            (minBoundary >= otherRange.minBoundary && minBoundary < otherRange.maxBoundary) ||
            (maxBoundary > otherRange.minBoundary && maxBoundary <= otherRange.maxBoundary) ||
            (minBoundary <= otherRange.minBoundary && maxBoundary >= otherRange.maxBoundary)
          ) {
            return `Range overlaps with [${otherRange.minBoundary}-${otherRange.maxBoundary}]`;
          }
        }
      }
    }
    return null;
  };

  const validateMinBoundary = (index) => ({ getFieldValue }) => ({
    validator(_, value) {
      const ranges = getFieldValue('ranges') || [];
      const currentRange = ranges[index];
      const maxBoundary = currentRange?.maxBoundary;

      // Make min boundary required
      if (value === undefined || value === null || value === '') {
        return Promise.reject(new Error('Min boundary is required'));
      }
      if (maxBoundary != null && value >= maxBoundary) {
        return Promise.reject(new Error('Min boundary must be less than Max boundary'));
      }
      if (value < 0) {
        return Promise.reject(new Error('Min boundary must be greater than or equal to 0'));
      }

      // Check for overlaps
      const overlapError = checkRangeOverlap(ranges, index, value, maxBoundary);
      if (overlapError) {
        return Promise.reject(new Error(overlapError));
      }

      return Promise.resolve();
    }
  });

  const validateMaxBoundary = (index) => ({ getFieldValue }) => ({
    validator(_, value) {
      const ranges = getFieldValue('ranges') || [];
      const currentRange = ranges[index];
      const minBoundary = currentRange?.minBoundary;

      // Make max boundary required
      if (value === undefined || value === null || value === '') {
        return Promise.reject(new Error('Max boundary is required'));
      }
      if (minBoundary != null && value <= minBoundary) {
        return Promise.reject(new Error('Max boundary must be greater than Min boundary'));
      }
      if (value < 0) {
        return Promise.reject(new Error('Max boundary must be greater than or equal to 0'));
      }

      // Check for overlaps
      const overlapError = checkRangeOverlap(ranges, index, minBoundary, value);
      if (overlapError) {
        return Promise.reject(new Error(overlapError));
      }

      return Promise.resolve();
    }
  });

  const handleAddRange = () => {
    const currentRanges = form.getFieldValue("ranges") || ranges || [];
    const newRange = {
      minBoundary: null,
      maxBoundary: null,
      minValue: null,
      maxValue: null,
      total: null,
      type: isEQUOrCRYPTO ? "quantity" : "lot",
    };
    const newRanges = [...currentRanges, newRange];
    setRanges(newRanges);
    form.setFieldsValue({ ranges: newRanges });
  };

  const handleRemoveRange = (index) => {
    const currentRanges = form.getFieldValue("ranges") || ranges || [];
    const newRanges = currentRanges.filter((_, i) => i !== index);
    setRanges(newRanges);
    form.setFieldsValue({ ranges: newRanges });
  };

  const handleFinish = (values) => {
    // Normalize undefined values to null for all range fields
    const normalizedRanges = (values.ranges || []).map(range => ({
      minBoundary: range.minBoundary ?? null,
      maxBoundary: range.maxBoundary ?? null,
      minValue: range.minValue ?? null,
      maxValue: range.maxValue ?? null,
      total: range.total ?? null,
      type: range.type || (isEQUOrCRYPTO ? "quantity" : "lot"),
    }));
    onSave(normalizedRanges);
  };

  return (
    <Modal
      title="Range Settings"
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      width={isMobile ? "95%" : 900}
      style={{ top: isMobile ? 20 : 100 }}
      okText="Save"
      cancelText="Cancel"
    >
      <Form
        form={form}
        onFinish={handleFinish}
        layout="vertical"
        initialValues={{ ranges: ranges }}
      >
        <Form.List name="ranges">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <div
                  key={field.key}
                  style={{
                    marginBottom: 16,
                    padding: 16,
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,

                  }}
                >
                  <Row gutter={16} align="middle">
                    <Col span={22}>
                      <Row gutter={16}>
                        {/* Select Range - give more width on each row */}
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            label="Select Range"
                            style={{ marginBottom: 0 }}
                          >
                            <Space.Compact style={{ width: "100%", display: "flex", alignItems: "stretch" }}>
                              <Form.Item
                                name={[field.name, "minBoundary"]}
                                rules={[validateMinBoundary(index)]}
                                dependencies={[['ranges']]}
                                style={{ marginBottom: 0, flex: 1 }}
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  controls={false}
                                  min={0}
                                  placeholder="Min"
                                />
                              </Form.Item>
                              <Space.Addon style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "0 8px",
                                height: "32px",
                                minHeight: "32px",

                                border: "1px solid #d9d9d9",
                                borderLeft: "none",
                                borderRight: "none",
                                fontSize: "12px",
                                fontWeight: 500,

                              }}> TO </Space.Addon>
                              <Form.Item
                                name={[field.name, "maxBoundary"]}
                                rules={[validateMaxBoundary(index)]}
                                dependencies={[['ranges']]}
                                style={{ marginBottom: 0, flex: 1 }}
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  min={0}
                                  controls={false}
                                  placeholder="Max"
                                />
                              </Form.Item>
                            </Space.Compact>
                          </Form.Item>
                        </Col>
                        {/* Type */}
                        <Col xs={24} sm={6} md={4}>
                          <Form.Item
                            label="Type"
                            name={[field.name, "type"]}
                          >
                            <Select style={{ width: "100%" }} placeholder="Select type">
                              <Select.Option value="lot">Lot</Select.Option>
                              <Select.Option value="quantity">Quantity</Select.Option>
                              <Select.Option value="value">Value</Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        {/* Min */}
                        <Col xs={24} sm={6} md={4}>
                          <Form.Item
                            label="Min"
                            name={[field.name, "minValue"]}
                            rules={[validateMinValue(index)]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              min={0}
                              placeholder="Min"
                            />
                          </Form.Item>
                        </Col>
                        {/* Max */}
                        <Col xs={24} sm={6} md={4}>
                          <Form.Item
                            label="Max"
                            name={[field.name, "maxValue"]}
                            rules={[validateMaxValue(index)]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              min={0}
                              placeholder="Max"
                            />
                          </Form.Item>
                        </Col>
                        {/* Total */}
                        <Col xs={24} sm={6} md={4}>
                          <Form.Item
                            label="Total"
                            name={[field.name, "total"]}
                            rules={[validateTotal(index)]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              min={0}
                              placeholder="Total"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      {/* Separate validation error display for Min/Max/Total requirement */}
                      <Form.Item
                        name={[field.name, "rangeValidation"]}
                        rules={[validateRangeValues(index)]}
                        dependencies={[
                          [field.name, "minValue"],
                          [field.name, "maxValue"],
                          [field.name, "total"]
                        ]}
                        style={{ marginTop: 8, marginBottom: 0 }}
                      >
                        <div style={{ display: "none" }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={2} style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      justifyContent: isMobile ? "flex-start" : "center",
                      marginTop: isMobile ? 8 : 0,
                      paddingTop: isMobile ? 0 : 0
                    }}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          handleRemoveRange(index);
                          remove(field.name);
                        }}
                        disabled={fields.length === 1}
                        style={{
                          height: isMobile ? "32px" : "auto",
                          width: isMobile ? "100%" : "auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      />
                    </Col>
                  </Row>
                </div>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => {
                    handleAddRange();
                    add();
                  }}
                  block
                  icon={<PlusOutlined />}
                >
                  Add Range
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};





